import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { devLog } from '../../utils/logger';
import { passwordResetApi } from '../../services/passwordResetApi';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    try {
      devLog.debug('📧 Sending password reset request for:', email);
      
      // Use the API service
      const result = await passwordResetApi.requestPasswordReset(email);
      
      if (result.status === 'success') {
        setIsSubmitted(true);
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      devLog.error('Password reset request failed:', error);
      toast.error('Failed to send reset instructions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.12),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_50%),linear-gradient(160deg,#f8fafc,#eef2ff_45%,#e0e7ff_90%)] dark:bg-[radial-gradient(circle_at_20%_20%,rgba(241,79,48,.15),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(31,159,132,.12),transparent_40%),linear-gradient(160deg,#070c17,#0f1727_40%,#111936_75%)] text-slate-900 dark:text-slate-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white/95 dark:bg-midnight-900/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/60 dark:shadow-soft p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 shadow-glow flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold font-heading text-slate-900 dark:text-white mb-2">
              Reset Your Password
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
          </div>

          {!isSubmitted ? (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl shadow-sm
                           placeholder-slate-400 dark:placeholder-slate-500
                           text-slate-900 dark:text-white
                           bg-white dark:bg-midnight-800
                           focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                           transition-colors duration-200"
                  placeholder="Enter your email address"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl
                         text-sm font-semibold text-white
                         bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500
                         disabled:from-brand-300 disabled:to-brand-400
                         disabled:cursor-not-allowed
                         transition-all duration-200
                         shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Instructions...
                  </div>
                ) : (
                  'Send Reset Instructions'
                )}
              </button>
            </form>
          ) : (
            /* Success State */
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-accent-600 dark:text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                Check Your Email
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                We've sent password reset instructions to:<br />
                <span className="font-medium text-slate-900 dark:text-white">{email}</span>
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-midnight-800 transition-colors"
                >
                  Try Different Email
                </button>
                <button
                  onClick={handleBackToLogin}
                  className="w-full px-4 py-2 bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500 text-white rounded-lg font-medium transition-all"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          {!isSubmitted && (
            <div className="text-center mt-6">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Remember your password?{' '}
                <Link
                  to="/login"
                  className="font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            If you don't receive an email within a few minutes, please check your spam folder.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
